import React, { useEffect, useCallback, useState } from 'react';
import {
  Box,
  Button,
  DialogContentText,
  CircularProgress
} from '@mui/material';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import { GradeScale } from '../../types/gradeScaleType';
import EditGradeScaleDialog from './EditGradeDialog';
import gradeScale from '../../apis/gradeScale';
import { HeaderBar, Title, AddGradeScaleButton } from './styled';
import { gradeScaleColumns } from './utils/gradeScaleColumns';
import { SnackbarSeverityType } from '../../enums';
import ToastMessage from '../../components/ToastMessage';
import AddGradeScaleDialog from './AddGradeScaleDialog';
import Loader from '../../components/Loader/Loader';
import DialogModal from '../../components/DialogModal';
import { DataGrid } from '@mui/x-data-grid';
import pallet from '../../layouts/pallet';
import { dataGridSx } from '../common/styled/DataGrid';
import Icons from '../../components/icons';
import tableColumnUtils from '../../utils/tableColumnUtils';
import { PageWrapper } from '../common/styled';

const GradeScalePage: React.FC = () => {
  const school = useSelector((state: RootState) => state.profile.user.school);
  const [gradeScales, setGradeScales] = useState<GradeScale[]>([]);
  const [addOpen, setAddOpen] = useState(false);
  const [editScale, setEditScale] = useState<GradeScale | null>(null);
  const [deleteGradeScale, setDeleteGradeScale] = useState<GradeScale | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastSeverity, setToastSeverity] = useState<SnackbarSeverityType>(
    SnackbarSeverityType.success
  );
  const [deleteLoading, setDeleteLoading] = useState(false);

  const handleCloseToast = () => {
    setToastOpen(false);
  };

  const showToast = useCallback(
    (message: string, severity: SnackbarSeverityType) => {
      setToastMessage(message);
      setToastSeverity(severity);
      setToastOpen(true);
    },
    []
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const data = await gradeScale.getGradeScales(school);
        setGradeScales(data as unknown as GradeScale[]);
      } catch (error) {
        console.error('Failed to fetch grade scales:', error);
        showToast('Failed to fetch grade scales.', SnackbarSeverityType.error);
      } finally {
        setLoading(false);
      }
    };

    if (school) {
      fetchData();
    }
  }, [school, showToast]);

  const handleOpenAdd = () => setAddOpen(true);
  const handleCloseAdd = () => setAddOpen(false);

  const columns = gradeScaleColumns(
    (scale: GradeScale) => setEditScale(scale),
    (scale: GradeScale) => setDeleteGradeScale(scale)
  );

  const addGradeScale = (scale: GradeScale) => {
    const id = (scale as any).id || (scale as any)._id;
    setGradeScales((prev) => [...prev, { ...scale, id }]);
  };

  const updateGradeScale = (scale: GradeScale) => {
    setGradeScales((prev) => prev.map((s) => (s.id === scale.id ? scale : s)));
  };

  const handleDeleteConfirm = useCallback(async () => {
    if (deleteGradeScale) {
      try {
        setDeleteLoading(true);
        if (!deleteGradeScale?.id) {
          console.error('Grade scale ID is undefined.');
          return;
        }
        await gradeScale.deleteGradeScale(deleteGradeScale.id);
        setGradeScales((prev) =>
          prev.filter((s) => s.id !== deleteGradeScale.id)
        );
        showToast(
          'Grade scale deleted successfully!',
          SnackbarSeverityType.success
        );
      } catch (error) {
        console.error('Error deleting grade scale:', error);
        showToast('Failed to delete grade scale.', SnackbarSeverityType.error);
      } finally {
        setDeleteLoading(false);
        setDeleteGradeScale(null);
      }
    }
  }, [deleteGradeScale, showToast]);

  return (
    <Box p={3}>
      <HeaderBar>
        <Title variant='h3' gutterBottom>
          Grade Scales
        </Title>
        <AddGradeScaleButton variant='contained' onClick={handleOpenAdd}>
          <Icons.PlusWhite style={{ marginRight: '6px' }} /> Add Grade Scale
        </AddGradeScaleButton>
      </HeaderBar>
      <Loader show={loading && gradeScales.length === 0} />
      <PageWrapper>
        <DataGrid
          rows={gradeScales}
          columns={columns}
          initialState={tableColumnUtils.DATA_GRID_INITIAL_STATE}
          pageSizeOptions={tableColumnUtils.PAGE_SIZE_OPTIONS}
          disableRowSelectionOnClick
          loading={loading}
          sx={dataGridSx(pallet)}
        />
      </PageWrapper>
      <AddGradeScaleDialog
        open={addOpen}
        onClose={handleCloseAdd}
        onSaved={addGradeScale}
        school={school}
        showToast={showToast}
      />

      {editScale && (
        <EditGradeScaleDialog
          open={!!editScale}
          onClose={() => setEditScale(null)}
          gradeScale={editScale}
          school={school}
          onSaved={updateGradeScale}
          showToast={showToast}
        />
      )}

      <DialogModal
        open={!!deleteGradeScale}
        onClose={() => setDeleteGradeScale(null)}
        title='Delete Grade Scale'
        actions={
          <>
            <Button
              onClick={() => setDeleteGradeScale(null)}
              color='primary'
              disabled={deleteLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              color='primary'
              autoFocus
              disabled={deleteLoading}
            >
              {deleteLoading ? (
                <CircularProgress size={20} color='inherit' />
              ) : (
                'Delete'
              )}
            </Button>
          </>
        }
      >
        <DialogContentText>
          Are you sure you want to delete grade scale "{deleteGradeScale?.name}
          "?
        </DialogContentText>
      </DialogModal>
      <ToastMessage
        open={toastOpen}
        message={toastMessage}
        severity={toastSeverity}
        onClose={handleCloseToast}
      />
    </Box>
  );
};

export default GradeScalePage;
